/* Site v2 — carrtech.ai analytics. Keeps the v1 core (4 StatCards · requests
   sparkline · top pages) and expands it: a labeled page filter (client-side —
   header stats + the top-pages list recompute; aggregate widgets stay marked
   "site-wide"), the 7-day unique-visitor trend, Core Web Vitals (value + grade
   WORD + dot — color is never the only signal), sources / countries / devices
   share bars, the 404 table, and the form-conversion card (honest "not wired
   yet" when the counter is null). Every data region ships all four states via
   Widget/WidgetBody. Wired to Cloudflare zone + RUM GraphQL and Resend. */
import { useState } from 'react';
import { StatCard, Select, Badge, Table } from '../../ds';
import { Sparkline, CenterState, WidgetBody } from '../../ds/mc-kit';
import { useContract } from '../../lib/api';
import { Widget, Kicker } from '../ui';
import { integrationFor } from '../../lib/flags';
import { deriveTopPages } from '../../lib/selectors';
import type { NotFoundRow, ShareRow, VitalsGrade, WebVitals } from '../../lib/contract';

/* ---- Core Web Vitals grades: dot + WORD + tone — never color alone. ---- */
const GRADE: Record<VitalsGrade, { ink: string; dot: string; word: string }> = {
  good: { ink: 'var(--accent-ink)', dot: 'var(--accent)', word: 'good' },
  'needs-improvement': { ink: 'var(--warn)', dot: 'var(--warn)', word: 'needs improvement' },
  poor: { ink: 'var(--danger)', dot: 'var(--danger)', word: 'poor' },
};

const monoTiny: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' };
const ell: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

/* Share-bar list (sources / countries / devices) — same idiom as top pages. */
function ShareBars({ rows, emptyLabel, emptySub }: { rows: ShareRow[]; emptyLabel: string; emptySub?: string }) {
  if (!rows.length) return <CenterState mark="M12 5v14M5 12h14" label={emptyLabel} sub={emptySub} tone="muted" />;
  const max = Math.max(...rows.map((r) => r.visits));
  return (
    <div>
      {rows.map((r) => (
        <div key={r.label} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 5 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--ink)', minWidth: 0, ...ell }}>{r.label}</span>
            <span style={{ flex: 'none', ...monoTiny }}>{r.visits.toLocaleString()} · {r.pct}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 999, background: 'var(--surface2)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.max(2, Math.round((r.visits / max) * 100))}%`, borderRadius: 999, background: 'var(--accent)', boxShadow: '0 0 10px var(--glow-cyan)' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function VitalsRows({ v }: { v: WebVitals }) {
  const rows = [
    { key: 'LCP', name: 'Largest Contentful Paint', value: `${v.lcpMs.toLocaleString()}ms`, grade: v.lcpGrade },
    { key: 'CLS', name: 'Cumulative Layout Shift', value: v.cls.toFixed(3), grade: v.clsGrade },
    { key: 'INP', name: 'Interaction to Next Paint', value: `${v.inpMs.toLocaleString()}ms`, grade: v.inpGrade },
  ];
  return (
    <div>
      {rows.map((r, i) => {
        const g = GRADE[r.grade];
        return (
          <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
            <span style={{ width: 34, flex: 'none', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em', color: 'var(--muted)' }}>{r.key}</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--muted)', ...ell }}>{r.name}</span>
            <span style={{ flex: 'none', fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 17, letterSpacing: '-0.01em', color: 'var(--ink)' }}>{r.value}</span>
            <span style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: g.ink }}>
              <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: g.dot, flex: 'none' }} />
              {g.word}
            </span>
          </div>
        );
      })}
      <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>
        p75 · {v.sampled.toLocaleString()} pageloads sampled
      </div>
    </div>
  );
}

export function Site() {
  const site = useContract('site', { pollMs: 20_000 });
  const connect = integrationFor('site')?.connect;
  const [pageSel, setPageSel] = useState('all');

  const s = site.data;
  const pages = s?.topPages ?? [];
  // Guard: if the selected path leaves the data on a poll, fall back to all.
  const page = pageSel !== 'all' && pages.some((p) => p.path === pageSel) ? pageSel : 'all';
  const filtered = page === 'all' ? null : (pages.find((p) => p.path === page) ?? null);
  const siteWide = page !== 'all';
  // Aggregate widgets can't be sliced per-page — say so when a filter is on.
  const wideTag = siteWide
    ? <span style={{ ...monoTiny, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>site-wide</span>
    : undefined;

  return (
    <div data-screen-label="Site" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mc-gap)' }}>

      {/* === PAGE FILTER — a real, labeled, focusable control === */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', maxWidth: 360, minWidth: 0 }}>
          <Select
            label="Page filter"
            value={page}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPageSel(e.target.value)}
            hint={siteWide ? 'Header stats + top pages follow this page — aggregate widgets stay site-wide.' : undefined}
          >
            <option value="all">All pages</option>
            {pages.map((p) => (
              <option key={p.path} value={p.path}>{p.path}</option>
            ))}
          </Select>
        </div>
        <Kicker style={{ paddingTop: 8 }}>// carrtech.ai</Kicker>
      </div>

      {/* === ROW 1: 4 StatCards (auto grid — add more without relayout) === */}
      <WidgetBody state={site.state} minHeight={92} rows={1}
        emptyLabel="No traffic yet" errorLabel="Analytics offline" errorSub="Couldn't reach the edge logs." onRetry={site.refetch}>
        {s && (
          <div className="mc-auto mc-auto-md">
            {filtered ? (
              <StatCard label="Page views · 24h" value={filtered.views.toLocaleString()} delta={`${filtered.pct}% of site`} deltaTone="cyan" />
            ) : (
              <StatCard label="Visitors today" value={s.visitors.toLocaleString()} delta={s.visitorsDelta} deltaTone="cyan" />
            )}
            <StatCard label={siteWide ? 'Subscribers · site-wide' : 'Subscribers'} value={s.subscribers.toLocaleString()} delta={s.subsDelta} deltaTone="cyan" />
            <StatCard label={siteWide ? 'Uptime · site-wide' : 'Uptime · 30d'} value={s.uptime} delta="SLA ok" deltaTone="cyan" />
            <StatCard label={siteWide ? 'Error rate · site-wide' : 'Error rate'} value={s.errorRate} delta="watch" deltaTone="danger" />
          </div>
        )}
      </WidgetBody>

      {/* === ROW 2: requests sparkline (1.5fr) + top pages (1fr) === */}
      <div className="mc-row-2" style={{ '--mc-2': '1.5fr 1fr' } as React.CSSProperties}>
        <Widget query={site} title="// requests · 24h" label="Requests over 24 hours"
          minHeight={150} rows={3} connect={connect} badge="connect me"
          emptyLabel="No traffic yet" errorLabel="Analytics offline" errorSub="Couldn't reach the edge logs."
          aside={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              {wideTag}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-ink)', whiteSpace: 'nowrap' }}>
                <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 7px var(--accent)', animation: 'ct-pulse 2s ease-in-out infinite' }} />
                {s?.online ?? 0} on the site now
              </span>
            </span>
          }>
          {(d) => (
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 32, letterSpacing: '-0.02em', color: 'var(--ink)' }}>{d.requestsTotal}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--accent-ink)' }}>{d.requestsDelta}</span>
              </div>
              <Sparkline data={d.requests} height={120} tone="cyan" />
            </div>
          )}
        </Widget>

        <Widget query={site} title="// top pages · 24h" label="Top pages"
          minHeight={150} rows={4} connect={connect} badge="connect me"
          emptyLabel="No page views" errorLabel="Pages unavailable"
          aside={<span style={monoTiny}>{siteWide ? 'filtered' : `${pages.length} paths`}</span>}>
          {(d) => {
            const rows = deriveTopPages(d).filter((p) => page === 'all' || p.path === page);
            if (!rows.length) return <CenterState mark="M12 5v14M5 12h14" label="No page views" sub="RUM hasn't recorded a pageload yet." tone="muted" />;
            return (
              <div className="mc-scroll" tabIndex={0} role="region" aria-label="Top pages list" style={{ maxHeight: 264, overflow: 'auto', paddingRight: 4 }}>
                {rows.map((p) => (
                  <div key={p.path} style={{ marginBottom: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 5 }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--ink)', minWidth: 0, ...ell }}>{p.path}</span>
                      <span style={{ flex: 'none', ...monoTiny }}>{p.views.toLocaleString()} · {p.pct}%</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 999, background: 'var(--surface2)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${p.barPct}%`, borderRadius: 999, background: 'var(--accent)', boxShadow: '0 0 10px var(--glow-cyan)' }} />
                    </div>
                  </div>
                ))}
              </div>
            );
          }}
        </Widget>
      </div>

      {/* === ROW 3: unique visitors 7d (1.5fr) + Core Web Vitals (1fr) === */}
      <div className="mc-row-2" style={{ '--mc-2': '1.5fr 1fr' } as React.CSSProperties}>
        <Widget query={site} title="// unique visitors · 7d" label="Unique visitors, last 7 days"
          minHeight={150} rows={3} connect={connect} badge="connect me"
          emptyLabel="No visitor history" errorLabel="Trend unavailable" aside={wideTag}>
          {(d) =>
            d.uniquesTrend.length ? (
              <div>
                <Sparkline data={d.uniquesTrend.map((u) => u.uniques)} height={96} tone="iris" />
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${d.uniquesTrend.length}, 1fr)`, gap: 4, marginTop: 12 }}>
                  {d.uniquesTrend.map((u, i) => (
                    <div key={`${u.date}-${i}`} style={{ textAlign: 'center', minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--muted)', ...ell }}>{u.date}</div>
                      <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 13, color: 'var(--ink)', marginTop: 2, ...ell }}>{u.uniques.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 8, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>
                  per-day uniques — never summed across days
                </div>
              </div>
            ) : (
              <CenterState mark="M12 5v14M5 12h14" label="No visitor history yet" sub="The per-day uniques trend appears once the zone reports traffic." tone="muted" />
            )
          }
        </Widget>

        <Widget query={site} title="// core web vitals · 7d" label="Core Web Vitals"
          minHeight={150} rows={3} connect={connect} badge="connect me"
          emptyLabel="No RUM samples" errorLabel="Vitals unavailable" aside={wideTag}>
          {(d) =>
            d.vitals ? (
              <VitalsRows v={d.vitals} />
            ) : (
              <CenterState mark="M12 5v14M5 12h14" label="No RUM samples yet" sub="Real-user vitals appear once the Web Analytics beacon reports pageloads." tone="muted" />
            )
          }
        </Widget>
      </div>

      {/* === ROW 4: sources · countries · devices (auto grid — extensible) === */}
      <div className="mc-auto mc-auto-lg">
        <Widget query={site} title="// sources · 7d" label="Traffic sources"
          minHeight={150} rows={4} connect={connect} badge="connect me"
          emptyLabel="No referrer data" errorLabel="Sources unavailable" aside={wideTag}>
          {(d) => <ShareBars rows={d.sources} emptyLabel="No referrer data yet" emptySub="Sources appear once RUM sees inbound visits." />}
        </Widget>
        <Widget query={site} title="// countries · 7d" label="Visitors by country"
          minHeight={150} rows={4} connect={connect} badge="connect me"
          emptyLabel="No location data" errorLabel="Countries unavailable" aside={wideTag}>
          {(d) => <ShareBars rows={d.countries} emptyLabel="No location data yet" />}
        </Widget>
        <Widget query={site} title="// devices · 7d" label="Visitors by device"
          minHeight={150} rows={4} connect={connect} badge="connect me"
          emptyLabel="No device data" errorLabel="Devices unavailable" aside={wideTag}>
          {(d) => <ShareBars rows={d.devices} emptyLabel="No device data yet" />}
        </Widget>
      </div>

      {/* === ROW 5: 404s table (1.5fr) + form conversion (1fr) === */}
      <div className="mc-row-2" style={{ '--mc-2': '1.5fr 1fr' } as React.CSSProperties}>
        <Widget query={site} title="// 404s · 7d" label="Broken paths, last 7 days"
          minHeight={150} rows={3} connect={connect} badge="connect me"
          emptyLabel="No broken links" errorLabel="404 log unavailable" aside={wideTag}>
          {(d) =>
            d.notFound.length ? (
              <div className="mc-table-wrap mc-scroll" tabIndex={0} role="region" aria-label="404 paths table">
                <Table
                  columns={[
                    {
                      key: 'path',
                      label: 'path',
                      render: (r: NotFoundRow) => <span className="mc-wrap-anywhere" style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{r.path}</span>,
                    },
                    {
                      key: 'count',
                      label: 'hits',
                      align: 'right',
                      render: (r: NotFoundRow) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{r.count.toLocaleString()}</span>,
                    },
                  ]}
                  rows={d.notFound}
                />
              </div>
            ) : (
              <CenterState mark="M20 6 9 17l-5-5" label="No broken links" sub="Nothing 404'd at the edge this week." tone="muted" />
            )
          }
        </Widget>

        <Widget query={site} title="// form conversion" label="Contact form conversion"
          minHeight={150} rows={2} connect={connect} badge="connect me"
          emptyLabel="No form data" errorLabel="Form data unavailable" aside={wideTag}>
          {(d) =>
            d.formConversion ? (
              <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
                {[
                  { label: 'views', value: d.formConversion.views.toLocaleString() },
                  { label: 'submits', value: d.formConversion.submits.toLocaleString() },
                  { label: 'rate', value: d.formConversion.rate },
                ].map((c) => (
                  <div key={c.label} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)' }}>{c.label}</span>
                    <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 25, color: 'var(--ink)' }}>{c.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '18px 12px', textAlign: 'center' }}>
                <Badge tone="warn">coming soon</Badge>
                <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>Form counter not wired yet</span>
                <span style={{ fontSize: 12.5, color: 'var(--muted)', maxWidth: 300, lineHeight: 1.5 }}>
                  Pageloads are tracked, but the contact form has no submit counter — wire one and conversion shows up here. No sample numbers on live data.
                </span>
              </div>
            )
          }
        </Widget>
      </div>
    </div>
  );
}
